import { API_BASE } from './api-base';
// Shares the page's whole client-error budget (MAX_PER_PAGE = 3, see that file) with every other
// 'render'/'window'/'unhandled-rejection' report — a page that already hit three unrelated JS errors
// before a parked render ever expires or gets its final refusal drops this report with nothing to show
// for it. Accepted rather than given its own channel: this is a diagnostic trace for an already-rare
// edge case, not the thing that decides whether the player gets paid, and a dedicated, unthrottled
// pipe for it is a bigger piece of work than this fix calls for.
import { reportClientError } from './report-client-error';
import { CREDIT_MINER_CLAIM_ERRORS } from './credit-miner-claim-errors';
import { CREDITS_REFRESH_EVENT } from './use-credits';

/**
 * The Credit Miner's web plumbing: loading the game's static build, talking to its two API routes, and
 * handing the "what did this render pay" result from the placeholder that mined it to the picture that
 * replaces it.
 *
 * The game itself is `public/credit-miner/game.js` — one factory on `window`, the same file the native
 * apps render from their own canvases. It is loaded on first use, not bundled: it is a plain script
 * with its own stylesheet, and a chat that never shows a placeholder never pays for it.
 */
export interface CreditMinerState {
  life: number;
  renderEarned: number;
  px: number;
  lv: Record<string, number>;
  relics: number;
  mineIndex: number;
  deepest: number;
  /** Lifetime seconds played; the server turns the delta between two claims into "played this render". */
  time: number;
  deaths: number;
  trips: number;
  /** Every tile already mined out of the current mine, as `"x,y"` keys (original, pre-collapse row
   *  numbers — see `game.js`'s own `orig()`). Exists on the live game's real state; just never read from
   *  here before. */
  dug: string[];
  /** Original row numbers already collapsed out of the mine. */
  removed: number[];
  /** How many renders this account has EVER played — a lifetime counter, not reset per render. Gates
   *  `game.js`'s one-time early-render credit top-up (its first `EARLY_RENDER_CAP` renders only), so a BO
   *  replay seeding from this account's history needs the real count to reproduce that top-up correctly
   *  instead of guessing. */
  rendersPlayed: number;
  /** Where the miner avatar was standing, and what it was carrying, the moment this render ended — `null`
   *  before the very first save. `game.js` places the miner here (and restores its bag) at construction,
   *  so a replay seeded without it starts the miner at a default spot rather than where the real account
   *  actually left off. */
  miner: { x: number; y: number; nuggets: unknown[]; lamp: number } | null;
  /** How many renders in a row this account claimed within `game.js`'s own streak window — drives both the
   *  "🔥 N waits in a row" HUD toast and the ore payout multiplier for the NEXT render. */
  streak: number;
  /** When the current streak was last extended (epoch ms) — `game.js` uses this, not `streak` alone, to
   *  decide whether the NEXT render still counts as consecutive or resets the streak to 1. */
  streakAt: number;
  /** Which crystals along the intro trail this account has already collected, by trail index — without it,
   *  a replay re-offers crystals the real account picked up renders ago. */
  trailTaken: boolean[];
  /** Whether THIS render ended with the current mine already fully dug out, waiting on the player's own
   *  "reopen" click — a render can end there (backgrounded, tab closed) before that click ever happens.
   *  Without it, a replay seeded from such a claim shows a mine dug bare with no "cleared" banner, instead
   *  of the same modal the real account's next render actually opened to. */
  cleared: boolean;
}
/**
 * The frame-indexed input log a session's world evolved from: enough to replay it into a fresh game with
 * the same seed and watch roughly what happened, never anything the claim trusts. Scoped to whatever a
 * single game instance saw — a remount mid-render (the chat re-keys a row) starts a new instance with an
 * empty log, so a render that spans a remount is missing that earlier stretch; the claimed amount itself
 * is unaffected, since that comes from the persisted save, not from this log.
 */
export interface CreditMinerRecording {
  seed: number;
  frames: number;
  events: unknown[];
  /** A short hash of the `game.js` this session actually ran, so a later viewer can tell whether it is
   *  replaying against the same simulation code or a since-changed one. Null when it could not be
   *  computed in time — a viewer should treat that the same as a mismatch, not as a match. */
  gameVersion: string | null;
}
export interface CreditMinerGame {
  readonly state: CreditMinerState;
  readonly recording: CreditMinerRecording;
  destroy(): void;
  pause(): void;
  resume(): void;
  /** Lifts the opening screen as a tap on it would. */
  play(): void;
}
export interface CreditMinerOptions {
  renderId?: string;
  /** Open on the veil even when this render already lifted it, in an earlier page. */
  veil?: boolean;
  storageKey?: string | null;
  storage?: { load(): unknown; save(state: unknown): void; reset?(): void };
  fit?: { w: number; h: number };
  /** What is still pending toward the next whole credit as of this mount — a granted credit already joined the
   *  account's real balance, so it is never included here, and the hero number never becomes a lifetime score. */
  balance?: { bankedMicro: number };
  onLog?(kind: string, text: string, data?: Record<string, unknown>): void;
}
type CreateCreditMiner = (root: HTMLElement, opts: CreditMinerOptions) => CreditMinerGame;
declare global {
  interface Window {
    createCreditMiner?: CreateCreditMiner;
  }
}

/** The game's own layout; the placeholder and the picture that lands in it take this box. */
export const CREDIT_MINER_BOX = { w: 280, h: 224 } as const;

let loading: Promise<CreateCreditMiner> | null = null;

let styles: Promise<void> | null = null;
/** The game's stylesheet, once per page. Resolves when it has loaded, so the host can draw the game's own opening
 *  screen with it while the script and the saved progress are still on their way. */
export function loadCreditMinerStyles(basePath: string): Promise<void> {
  if (styles) return styles;
  styles = new Promise<void>((resolve) => {
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = `${basePath}/credit-miner/game.css`;
    css.onload = () => resolve();
    css.onerror = () => resolve();
    document.head.appendChild(css);
  });
  return styles;
}

let gameVersion: string | null = null;
/** Hashes the exact bytes served for `game.js`, once per page. Best-effort: a fetch failure or a browser
 *  without SubtleCrypto just leaves recordings unstamped rather than blocking the game on it. */
function loadGameVersion(src: string): void {
  void (async () => {
    try {
      const bytes = await (await fetch(src)).arrayBuffer();
      const digest = await crypto.subtle.digest('SHA-256', bytes);
      gameVersion = Array.from(new Uint8Array(digest).slice(0, 8), (b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
      // see above
    }
  })();
}

export function loadCreditMiner(basePath: string): Promise<CreateCreditMiner> {
  if (typeof window !== 'undefined' && window.createCreditMiner) return Promise.resolve(window.createCreditMiner);
  if (loading) return loading;
  loading = new Promise<CreateCreditMiner>((resolve, reject) => {
    void loadCreditMinerStyles(basePath);
    const script = document.createElement('script');
    script.src = `${basePath}/credit-miner/game.js`;
    script.async = true;
    script.onload = () => (window.createCreditMiner ? resolve(window.createCreditMiner) : reject(new Error('credit-miner: game.js loaded without its factory')));
    script.onerror = () => reject(new Error('credit-miner: game.js failed to load'));
    document.head.appendChild(script);
    loadGameVersion(script.src);
  });
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

export interface CreditMinerProgress {
  enabled: boolean;
  progress: unknown | null;
  claimedMicro: number;
  bankedMicro: number;
  grantedCredits: number;
  pendingCredits: number;
  renders: number;
  /** When the server last wiped this account, if ever — see `freshestCreditMinerProgress`. */
  resetAt: string | null;
}

export async function fetchCreditMinerProgress(timeoutMs = 4000): Promise<CreditMinerProgress | null> {
  try {
    const res = await fetch(`${API_BASE}/api/credit-miner/progress`, { credentials: 'include', signal: AbortSignal.timeout(timeoutMs) });
    if (!res.ok) return null;
    const progress = (await res.json()) as CreditMinerProgress;
    if (progress.enabled) lastBankedMicro = progress.bankedMicro;
    return progress;
  } catch {
    return null;
  }
}

/** The banked fraction the server last reported on this page, from a progress read or a claim: what a game
 *  starts from when its own read did not come back in time, instead of a bank of zero. */
let lastBankedMicro: number | null = null;
export const rememberedBankedMicro = (): number | null => lastBankedMicro;

/** True once a game on this page has saved: a game whose progress read failed can resume from that save and
 *  keep saving, where one with nothing to resume from would start a fresh mine and overwrite the real one. */
export const hasSaveInThisTab = (): boolean => lastSaved !== null;

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSave: unknown = null;
/** The game saves every few seconds and on every bank; the server sees the latest state at most every 4 s. */
/** The newest state this tab saved, sent or not: a remount resumes from it rather than from a server read that
 *  may predate the last flush. */
let lastSaved: unknown = null;

/**
 * The save a game should resume from: this tab's newest one, unless the server's is fresher (another tab or
 * device played since). Freshness is the game's own `lastSeen` stamp, written on every save.
 *
 * `resetAt`, when given, is a floor under that: a reset moves the account's own clock forward, and nothing
 * this tab remembers from BEFORE it may outrank the fresh (empty) server state — the tab could easily
 * still be holding an in-memory save from minutes ago, stamped with a real, recent `lastSeen`, and an
 * emptied server row has nothing of its own to compare that against. Once this tab saves again, its new
 * stamp is naturally after `resetAt` and the ordinary comparison below takes back over on its own — a
 * reset needs no cleanup here, just this floor on what counts.
 */
export function freshestCreditMinerProgress(server: unknown, resetAt?: string | null): unknown {
  const seen = (save: unknown) => {
    const stamp = (save as { lastSeen?: unknown } | null)?.lastSeen;
    return typeof stamp === 'number' && Number.isFinite(stamp) ? stamp : -1;
  };
  const resetMs = resetAt ? Date.parse(resetAt) : NaN;
  if (Number.isFinite(resetMs) && seen(lastSaved) < resetMs) return server ?? null;
  if (lastSaved === null) return server ?? null;
  if (server === null || server === undefined) return lastSaved;
  return seen(lastSaved) >= seen(server) ? lastSaved : server;
}

export function saveCreditMinerProgress(progress: unknown): void {
  lastSaved = progress;
  pendingSave = progress;
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    void flushCreditMinerProgress();
  }, 4000);
}
export async function flushCreditMinerProgress(): Promise<void> {
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  const progress = pendingSave;
  pendingSave = null;
  if (!progress) return;
  try {
    await fetch(`${API_BASE}/api/credit-miner/progress`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      // No `keepalive`: the browser refuses keepalive bodies over 64 KB and a mine's dug map can approach that.
      // A save lost to navigation costs a few seconds of digging; the next one carries the same state.
      body: JSON.stringify({ progress }),
    });
  } catch {
    // see above
  }
}

export interface CreditMinerClaim {
  requestedMicro: number;
  claimedMicro: number;
  bankedMicro: number;
  wholeCredits: number;
  pendingCredits: number;
  grantedCredits: number;
  renders: number;
}

/** The snapshot's whole JSON must stay under the harness's own claim-size cap (2048 bytes, enforced
 *  server-side on `body.snapshot`) — a claim is real money settling, and no amount of replay fidelity is
 *  worth risking that over a byte count. A generous margin below the hard cap, not the cap itself, since
 *  this is computed before the request is actually serialized alongside the rest of the claim body. */
const DUG_SNAPSHOT_BYTE_BUDGET = 1600;

/** Which tiles are dug compresses into a bitmask sized to the tiles' own bounding box — `w` (columns) and
 *  `y0` (the lowest original row touched) — rather than a fixed reservation for the mine's theoretical max
 *  size. This needs no constant mirrored from `game.js` (column count, max depth) kept in sync by hand: the
 *  box the player actually dug is self-describing, and is usually far smaller than the mine's full extent.
 *  `undefined` for an untouched mine — nothing to encode, and no reason to send an empty shape. */
function encodeDug(dug: string[]): { w: number; y0: number; bits: string } | undefined {
  if (!dug || dug.length === 0) return undefined;
  const cells: [number, number][] = [];
  let minY = Infinity;
  let maxX = 0;
  let maxY = -Infinity;
  for (const key of dug) {
    const match = /^(\d+),(\d+)$/.exec(key);
    if (!match) continue; // a corrupt entry says nothing about the shape of the rest
    const x = Number(match[1]);
    const y = Number(match[2]);
    cells.push([x, y]);
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (cells.length === 0) return undefined;
  const w = maxX + 1;
  const h = maxY - minY + 1;
  const byteLength = Math.ceil((w * h) / 8);
  // The regex above bounds the SHAPE of a coordinate, not its SIZE — a malformed entry with an
  // implausibly long digit run (`Number(...)` overflowing to `Infinity`) blows the bounding box up before
  // this ever checks against the real byte budget below, and `new Uint8Array` throws on an allocation this
  // large rather than letting that budget check run. A box already past the budget would be dropped anyway
  // once encoded, so bailing out here first avoids the allocation without changing what ships.
  if (byteLength > DUG_SNAPSHOT_BYTE_BUDGET) return undefined;
  const bytes = new Uint8Array(byteLength);
  for (const [x, y] of cells) {
    const bit = (y - minY) * w + x;
    bytes[bit >> 3] |= 1 << (bit & 7);
  }
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return { w, y0: minY, bits: btoa(binary) };
}

/** What the Maestro gets to read about the player, sent with every claim. Client's word; never money.
 *  `dug`/`removed` ride along the same way, so a later replay can start from the real mine instead of an
 *  empty one — best-effort only: a pathological mine that would blow the claim's own byte budget just to
 *  carry its dig history drops `dug`/`removed` and keeps everything else, rather than risk the claim
 *  itself (real settlement) over a cosmetic replay improvement. */
export function creditMinerSnapshot(state: CreditMinerState) {
  const base = {
    lv: state.lv,
    px: state.px,
    relics: state.relics,
    mine: state.mineIndex + 1,
    deepest: state.deepest,
    life: state.life,
    time: state.time,
    deaths: state.deaths,
    trips: state.trips,
    // How much THIS render had earned by the moment it was claimed — what a replay's seeded `newWait()`
    // needs to reproduce the early-render top-up for the NEXT render, the same way the real account's own
    // play did. Always a real number; this field predates `rendersPlayed` by a long way.
    renderEarned: state.renderEarned,
    // Left OUT entirely (not defaulted to 0) when the running game.js instance predates this field: a BO
    // replay reads a present `0` as "this account has never played before" and wrongly re-applies the
    // early-render bonus, where an absent field correctly falls back to treating the account as not new.
    ...(Number.isFinite(state.rendersPlayed) ? { rendersPlayed: state.rendersPlayed } : {}),
    // Where the miner stood, the streak, and the crystal trail — small, fixed-size fields game.js restores
    // at construction time (before a single replay event plays), so without them a replay starts the miner
    // at the wrong spot, drops the streak multiplier, and re-offers crystals already collected.
    miner: state.miner,
    streak: state.streak,
    streakAt: state.streakAt,
    trailTaken: state.trailTaken,
    // Whether this render ended with the mine already cleared, waiting on a "reopen" click that never
    // happened — a replay seeded without it shows a bare-dug mine with no cleared modal.
    cleared: state.cleared,
  };
  // A rescued or lamp-out bag can leave dozens of uncollected nuggets — the one field above that isn't
  // small by construction, and `base` is what every return path below can end up sending alone, with no
  // size check after this one. Drop just the nuggets (not `miner` itself — the position and lamp are tiny
  // and still worth keeping, and not the whole snapshot) rather than risk the claim over a visual detail a
  // replay can live without.
  if (base.miner && JSON.stringify(base).length > DUG_SNAPSHOT_BYTE_BUDGET) {
    base.miner = { ...base.miner, nuggets: [] };
  }
  const dug = encodeDug(state.dug);
  // `hydrate()` (game.js) sanitizes a loaded `dug` back to `[]` if it isn't a real array, but has no
  // equivalent guard for `removed` — a save from before that field existed, or any hand-edited/corrupted
  // one, could hand this a non-array. `state` is a plain object from untyped JS; nothing enforces the TS
  // type at runtime, and this runs on every real claim, so guard it here rather than trust the shape.
  const removed = Array.isArray(state.removed) ? state.removed : [];
  // `dug` alone is NOT the right gate: `reopenMine()` (game.js) clears `dug` to `[]` on every relic-funded
  // reopen while leaving `removed` non-empty (the new mine's trimmed-off top rows) — a claim snapshotted in
  // that window, before the player digs a single new tile, has real `removed` data worth keeping even
  // though there is nothing yet to encode into `dug`.
  if (!dug && removed.length === 0) return base;
  const withDug = { ...base, dug, removed };
  if (JSON.stringify(withDug).length <= DUG_SNAPSHOT_BYTE_BUDGET) return withDug;
  return base;
}

/**
 * `claimed` is the only outcome with money attached. `retry` means this attempt answered nothing final —
 * nothing delivered yet to pay against, a dropped request, a timeout, or a transient server failure — and
 * asking again later might get a different answer. `done` means the server gave a FINAL no that asking
 * again can never change: this exact render already settled (whoever got there first collected it; a
 * render has one correlation id and pays out once), or the request itself was malformed in a way that is
 * not going to fix itself. Retrying a `done` outcome would just spend the render's whole park window
 * re-confirming the same refusal.
 */
type ClaimOutcome = { kind: 'claimed'; claim: CreditMinerClaim } | { kind: 'retry' } | { kind: 'done' };

/** The harness's own error codes for the two refusals that mean "stop asking" — see
 *  `packages/orion-harness-poc/src/endpoints/credit-miner.ts`, whose codes the Java harness answers too. Every other 4xx/5xx/network failure is
 *  `retry`: the harness is the only side that knows whether a render is actually unclaimable, so a code
 *  this client does not recognize is treated as "ask again" rather than guessed as terminal. */
const TERMINAL_CLAIM_ERRORS = new Set(CREDIT_MINER_CLAIM_ERRORS);

export async function claimCreditMinerRender(conversationId: string, microcents: number, snapshot: unknown, recording?: CreditMinerRecording): Promise<ClaimOutcome> {
  try {
    const res = await fetch(`${API_BASE}/api/credit-miner/claim`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId, microcents: Math.round(microcents), snapshot, recording }),
      keepalive: true,
      // Without a ceiling, a hung request holds `attemptClaim`'s in-flight lock for the rest of the page's
      // life — every later unmount for the same render would then see it as "already being handled" and
      // never try again, retry backoff included. Matches `fetchCreditMinerProgress`'s own ceiling.
      signal: AbortSignal.timeout(20_000),
    });
    if (!res.ok) {
      // The relay answers a refusal as `{ error: 'upstream_error', code }` (lib/credit-miner-harness.ts).
      const body = (await res.json().catch(() => null)) as { code?: string } | null;
      return { kind: body?.code && TERMINAL_CLAIM_ERRORS.has(body.code) ? 'done' : 'retry' };
    }
    const claim = (await res.json()) as CreditMinerClaim;
    lastBankedMicro = claim.bankedMicro;
    // A whole credit reached the balance: the sidebar's count re-polls the same way it does after a purchase.
    if (claim.wholeCredits > 0 && typeof window !== 'undefined') window.dispatchEvent(new Event(CREDITS_REFRESH_EVENT));
    return { kind: 'claimed', claim };
  } catch {
    return { kind: 'retry' };
  }
}

/**
 * The placeholder is unmounting. That is NOT always the picture arriving: the chat re-keys and remounts
 * rows mid-render too, and this function does not try to tell the two apart — it only PARKS the render:
 * `destroy()` first (it banks the bag, which belongs to this render), the save is flushed, and what the
 * render has mined so far waits in memory. A remount of the same render continues from the save and parks
 * again with the newer amount. Whether this is the real end of the render is for `claimLandedCreditMinerRender`
 * (and, ultimately, the harness) to decide — every unmount tries it, a remount's attempt is simply refused.
 */
export function parkCreditMinerRender(game: CreditMinerGame, key: string, conversationId: string | undefined, lastFrame?: string): void {
  game.destroy();
  if (lastFrame) rememberLastFrame(key, lastFrame);
  void flushCreditMinerProgress();
  if (!conversationId) return;
  // A render's earnings only grow, so a remount that resumed from an older save never lowers what is parked.
  const earlier = parked.get(key);
  if (earlier && earlier.minedMicro > game.state.renderEarned) return;
  parked.set(key, {
    conversationId,
    minedMicro: game.state.renderEarned,
    snapshot: creditMinerSnapshot(game.state),
    recording: { ...game.recording, gameVersion },
  });
  clearTimeout(parkedExpiry.get(key));
  scheduleExpiry(key, PARKED_RENDER_TTL_MS);
}

/** How soon to look again when the TTL fires exactly while an attempt is mid-flight — bounded by
 *  `claimCreditMinerRender`'s own 20s fetch ceiling, so the in-flight attempt has almost certainly
 *  settled one way or another by the time this fires, rather than bailing out with nothing to ever
 *  check again (which silently removed the 30-minute ceiling for a render unlucky enough to be
 *  mid-retry at the boundary — caught in review, not shipped). */
const TTL_RECHECK_DELAY_MS = 20_000;

function scheduleExpiry(key: string, delayMs: number): void {
  const expiry = setTimeout(() => {
    // An attempt still in flight right at the TTL boundary gets to finish rather than being raced: its
    // own settle handler does this exact cleanup (including the report below) once it knows the real
    // outcome, where firing both here and there could report a loss on a claim about to succeed. Looking
    // again shortly — rather than giving up here — is what keeps the 30-minute ceiling real for a render
    // whose retry loop happens to still be in flight at the boundary, instead of retrying forever because
    // nothing was ever scheduled to check again.
    if (claiming.has(key)) {
      scheduleExpiry(key, TTL_RECHECK_DELAY_MS);
      return;
    }
    // This is the one way a real play now vanishes: every claim attempt retries for the full 30 minutes,
    // so reaching this line means the harness never once said this render was delivered. Worth a trace —
    // a prior incident went unnoticed for this exact reason until a user happened to report it.
    const unclaimed = parked.get(key);
    if (unclaimed && unclaimed.minedMicro >= CELEBRATED_MICRO) {
      reportClientError('credit-miner-unclaimed', new Error(`render expired unclaimed, minedMicro=${unclaimed.minedMicro}, conversationId=${unclaimed.conversationId}`));
    }
    parked.delete(key);
    parkedExpiry.delete(key);
    racedThisStory.delete(key);
  }, delayMs);
  // A timer that only tidies memory must not keep a server process (or a test run) alive.
  (expiry as { unref?: () => void }).unref?.();
  parkedExpiry.set(key, expiry);
}

/** A render still unlanded after this long was retracted or abandoned; its parked claim is dropped. */
const PARKED_RENDER_TTL_MS = 30 * 60_000;
const parkedExpiry = new Map<string, ReturnType<typeof setTimeout>>();

interface ParkedRender {
  conversationId: string;
  minedMicro: number;
  snapshot: ReturnType<typeof creditMinerSnapshot>;
  recording: CreditMinerRecording;
}
const parked = new Map<string, ParkedRender>();

/** Below this, the amount shows in the running total, not as its own celebration — the same floor the game's
 *  own float text, and both native apps' landed badge, already draw the line at. */
const CELEBRATED_MICRO = 500;

/**
 * A `retry` outcome is re-attempted on this interval until the harness agrees there is something to pay
 * against, or the render's own park window (`PARKED_RENDER_TTL_MS`) gives up on it. There is no other
 * signal on this side that ever tells the client "the picture has truly landed" — the harness's own
 * delivery record is the only one that was ever going to be right, so the client just keeps asking instead
 * of guessing once.
 */
const CLAIM_RETRY_MS = 15_000;
/** A render currently mid-attempt, so a second call for the same key (another unmount racing this one)
 *  does not fire a second, overlapping request before the first has even answered. It does not mean the
 *  second call is lost: `attemptClaim`'s own settle handler re-checks `parked` every time and fires again
 *  immediately if anything about it changed while the request was in flight (see the comment there). */
const claiming = new Set<string>();
/** A render waiting out its retry delay. A fresh call for the same key is a better reason to try again
 *  right now than to sit out the rest of a backoff nobody asked for. */
const retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
/** A key whose render has been superseded by a bigger park at least once during its CURRENT claim story.
 *  Set the moment it happens and read only at the end (claimed-and-cleared, or `done`): the attempt that
 *  finally settles a key was, by definition, never itself superseded (nothing came after it to supersede
 *  it with) — so asking "was THIS attempt superseded" always answers no for the one attempt whose answer
 *  actually matters, which is the whole reason this is tracked per KEY and not re-derived per attempt. */
const racedThisStory = new Set<string>();

/**
 * `parked`, `claiming`, `retryTimers`/`parkedExpiry` and `racedThisStory` are four separate collections
 * rather than one `Map<key, State>` record, considered and kept apart rather than consolidated: `parked`
 * already answers "what would a claim for this key send" on its own and is read from render/attachment
 * code paths with no reason to see the other three; `claiming` is a lock that must be checked and cleared
 * strictly before `parked` is ever read for a new attempt, which a single shared object makes easier to
 * read out of order rather than harder; and `parkCreditMinerRender` replaces `parked`'s entry wholesale on
 * every re-park (the exact identity-replacement behavior `supersededByNewerPark`'s value comparison exists
 * to see past), so a flag living inside that entry — the natural shape of a consolidated record — would
 * need its own explicit carry-forward across every replacement, which is the same class of "state read
 * from the wrong place" bug this file has twice now had to fix, not an escape from it. Four collections
 * means four things to remember to clean up at each exit, which is real cost; one record means every read
 * of `parked` quietly becomes a read of lock/timer/flag state nothing asked for at that call site. Neither
 * is free — this chose fewer places to misread state over fewer collections to clean up.
 */

/**
 * The placeholder that mined these credits went away: claim what it mined and hand the result to the badge.
 * Nothing parked (the game never ran here, or the page reloaded in between) claims nothing, and neither
 * does a render that mined nothing worth celebrating — a badge announcing "+0.000 credits mined" is not the
 * shimmer this game is supposed to be. A `retry` outcome keeps the render parked and tries again; the
 * render is only ever dropped by its own TTL or a `done` (final) outcome, never by a request that didn't land.
 *
 * The badge is shown only once the SERVER has answered, never before: an attempt can be this render's real
 * end or a harmless remount the harness is about to refuse, and there is no way to tell those apart from
 * here. Showing a number before the server confirms one is exactly how a refused remount would still have
 * flashed "credits mined" at the player — the one dishonesty this function exists to not repeat.
 *
 * Considered and rejected: delaying the FIRST attempt for a key by a short window, so a near-simultaneous
 * remount and real ending would coalesce into one request for the final amount instead of racing two. That
 * would narrow (not close — the harness still pays a render's one correlation id to whichever claim lands
 * first) the bounded residue `racedThisStory` exists to measure, but at the cost of delaying every ordinary
 * claim by that same window, for a race this PR's actual motivating incident never contained: that incident
 * was a single attempt skipped outright (the fragile `delivered` guess this file no longer makes), not two
 * attempts racing each other — the amounts involved there were minutes apart, far wider than any delay
 * worth adding here. Nothing rules out revisiting this if `racedThisStory`'s own telemetry ever shows the
 * residue is not as rare as assumed; until it does, this is a narrower tool for a problem not yet observed.
 */
export function claimLandedCreditMinerRender(key: string): void {
  const pendingRetry = retryTimers.get(key);
  if (pendingRetry) {
    clearTimeout(pendingRetry);
    retryTimers.delete(key);
  }
  // An attempt already in flight for this key will itself notice and re-fire if `parked` has changed by
  // the time it settles (below) — this call does not need to, and firing a second overlapping request
  // here would only risk the smaller/older of the two reaching the harness first and settling the render
  // before the bigger/newer one ever gets sent.
  if (claiming.has(key)) return;
  attemptClaim(key);
}

function attemptClaim(key: string): void {
  const render = parked.get(key);
  if (!render) return;
  claiming.add(key);
  void claimCreditMinerRender(render.conversationId, render.minedMicro, render.snapshot, render.recording).then((outcome) => {
    claiming.delete(key);
    // A newer park may have replaced `render` while this attempt was in flight. This attempt's own answer
    // still applies to the amount IT asked about, but it is never the last word on the key: whatever is
    // parked now — unchanged, newer, or nothing — decides what happens next, not what this attempt expected.
    // Compared by VALUE, not by object reference: a remount that mined nothing new between two unmounts
    // still produces a fresh object (`parkCreditMinerRender` always writes one), and treating that as
    // "superseded" would fire a pointless follow-up claim the harness correctly refuses as already settled
    // — which this file would then misreport as a real loss, exactly the number this logging exists to get
    // right.
    const current = parked.get(key);
    const supersededByNewerPark = current !== undefined && current.minedMicro > render.minedMicro;
    if (supersededByNewerPark) racedThisStory.add(key);
    if (outcome.kind === 'claimed') {
      if (!supersededByNewerPark) {
        parked.delete(key);
        clearTimeout(parkedExpiry.get(key));
        parkedExpiry.delete(key);
        racedThisStory.delete(key);
      }
      if (outcome.claim.claimedMicro >= CELEBRATED_MICRO || outcome.claim.wholeCredits > 0) {
        rememberLanded(key, { minedMicro: outcome.claim.claimedMicro, wholeCredits: outcome.claim.wholeCredits });
      }
      // A bigger park replaced this one before it settled: the harness just told us this render's one
      // correlation id is now spent, so the newer amount cannot be collected by asking again — but it must
      // not sit un-retried until the TTL silently drops it either. One more honest attempt tells the
      // harness what is actually true: accepted (a genuinely later render under the same key, however
      // unlikely) or refused as already settled, in which case `done` below clears it for good.
      if (supersededByNewerPark) attemptClaim(key);
      return;
    }
    if (outcome.kind === 'done' || !current) {
      // A final "no" with a non-trivial amount still parked is the one loss this retry loop cannot close
      // by asking again: the harness settles a render's one correlation id on the first accepted claim, so
      // once that has happened — most plausibly because a smaller, earlier attempt for this same key won
      // the race, `racedThisStory` — nothing later can collect the rest. Worth tracing to learn how often
      // it actually happens, which is exactly the number nobody had when this shipped. Read `racedThisStory`
      // here, not `supersededByNewerPark`: the attempt that finally settles a key was, by construction,
      // never itself superseded (there is nothing after it to supersede it with), so the per-attempt flag
      // would always read false for the one attempt whose answer is the one worth logging.
      if (outcome.kind === 'done' && current && current.minedMicro >= CELEBRATED_MICRO) {
        reportClientError(
          'credit-miner-unclaimed',
          new Error(`render claim finally refused, strandedMicro=${current.minedMicro}, racedAnotherAttempt=${racedThisStory.has(key)}, conversationId=${current.conversationId}`),
        );
      }
      racedThisStory.delete(key);
      parked.delete(key);
      clearTimeout(parkedExpiry.get(key));
      parkedExpiry.delete(key);
      return;
    }
    const timer = setTimeout(() => {
      retryTimers.delete(key);
      attemptClaim(key);
    }, CLAIM_RETRY_MS);
    (timer as { unref?: () => void }).unref?.();
    retryTimers.set(key, timer);
  });
}

/**
 * The placeholder that mined the credits unmounts the moment the picture arrives, and the picture is a
 * different component; this is how the number crosses over. Kept for a minute, and updated once the server
 * has settled the claim so the badge can go from "mined" to "added".
 */
export interface LandedResult {
  minedMicro: number;
  wholeCredits?: number;
}
const landed = new Map<string, LandedResult>();
const listeners = new Map<string, Set<(r: LandedResult) => void>>();

/**
 * Renders whose veil was lifted on THIS page. A reload resumes the same render and asks again, the way a new
 * render does; a row the chat remounts mid-render does not ask twice.
 */
const veilLifted = new Set<string>();
export const veilLiftedOnThisPage = (key: string): boolean => veilLifted.has(key);
export function rememberVeilLifted(key: string): void {
  veilLifted.add(key);
}

/**
 * A render is its message: the game runs only in a message holding one picture, and the fill that lands the
 * picture renames it (the placeholder "puppy" arrives as "puppy.png"), so the file name cannot pair the two.
 */
export const landedKey = (messageId: string | undefined) => messageId ?? '';

/**
 * The game's last frame, shown where it played while the picture's bytes load, so the box goes from the mine
 * straight to the picture instead of through the generic image shimmer.
 */
const lastFrames = new Map<string, string>();
const lastFrameExpiry = new Map<string, ReturnType<typeof setTimeout>>();
function rememberLastFrame(key: string, frame: string): void {
  lastFrames.set(key, frame);
  // A remount parks again with a newer frame; the older timer must not take the newer frame with it.
  clearTimeout(lastFrameExpiry.get(key));
  const expiry = setTimeout(() => {
    lastFrames.delete(key);
    lastFrameExpiry.delete(key);
  }, PARKED_RENDER_TTL_MS);
  (expiry as { unref?: () => void }).unref?.();
  lastFrameExpiry.set(key, expiry);
}
export const readLastFrame = (key: string): string | undefined => lastFrames.get(key);

export function rememberLanded(key: string, result: LandedResult): void {
  landed.set(key, result);
  listeners.get(key)?.forEach((cb) => cb(result));
  const expiry = setTimeout(() => landed.delete(key), 60_000);
  (expiry as { unref?: () => void }).unref?.();
}

export function readLanded(key: string): LandedResult | undefined {
  return landed.get(key);
}

export function onLanded(key: string, cb: (r: LandedResult) => void): () => void {
  const set = listeners.get(key) ?? new Set();
  set.add(cb);
  listeners.set(key, set);
  return () => {
    set.delete(cb);
    if (!set.size) listeners.delete(key);
  };
}
